import React, { useEffect } from 'react';
import { Modal, Form, Input, message, Button } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { doctorService } from '../services';
import { Doctor } from '../types';
import './DoctorModal.css';

interface DoctorModalProps {
  visible: boolean;
  doctor: Doctor | null;
  onClose: () => void;
}

const DoctorModal: React.FC<DoctorModalProps> = ({ visible, doctor, onClose }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: (values: Partial<Doctor> & { id?: string }) => {
      if (doctor && doctor.id) {
        return doctorService.update(doctor.id.toString(), values);
      } else {
        return doctorService.create(values as Doctor);
      }
    },
    onSuccess: () => {
      message.success(doctor ? 'Cập nhật bác sĩ thành công!' : 'Thêm bác sĩ thành công!');
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
      onClose();
      form.resetFields();
    },
    onError: () => {
      message.error(doctor ? 'Cập nhật bác sĩ thất bại!' : 'Thêm bác sĩ thất bại!');
    },
  });

  useEffect(() => {
    if (visible) {
      if (doctor) {
        form.setFieldsValue(doctor);
      } else {
        form.resetFields();
      }
    }
  }, [visible, doctor, form]);

  const onFinish = (values: any) => {
    saveMutation.mutate(values);
  };

  return (
    <Modal
      title={doctor ? 'Sửa thông tin bác sĩ' : 'Thêm bác sĩ'}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      className="doctor-modal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={doctor || {}}
      >
        <Form.Item
          name="name"
          label="Tên bác sĩ"
          rules={[{ required: true, message: 'Vui lòng nhập tên bác sĩ!' }]}
        >
          <Input placeholder="Nhập tên bác sĩ" />
        </Form.Item>

        <Form.Item name="license_number" label="Số CCHN">
          <Input placeholder="Nhập số CCHN" />
        </Form.Item>

        <Form.Item name="phone" label="Điện thoại">
          <Input placeholder="Nhập số điện thoại" />
        </Form.Item>

        <Form.Item name="email" label="Email">
          <Input type="email" placeholder="Nhập email" />
        </Form.Item>

        <Form.Item
          name="national_rx_user"
          label="User trên Đơn Thuốc Quốc Gia"
          rules={[{ required: true, message: 'Vui lòng nhập User trên Đơn Thuốc Quốc Gia!' }]}
        >
          <Input placeholder="Nhập User trên Đơn Thuốc Quốc Gia" />
        </Form.Item>

          <Form.Item
            name="national_prescription_password"
            label="Mật khẩu trên Đơn Thuốc Quốc Gia"
            rules={[{ required: !doctor, message: 'Vui lòng nhập mật khẩu!' }]}
          >
            <Input.Password placeholder="Nhập mật khẩu" />
          </Form.Item>
        

        <Form.Item name="notes" label="Ghi chú">
          <Input.TextArea rows={4} placeholder="Nhập ghi chú (nếu có)" />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={saveMutation.isPending}
            block
          >
            Lưu thông tin
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DoctorModal;

